<template>
  <div>
    <PublicHeader v-bind="$props" />
    <div class="p-col-12 min-vh-50 mt-6rem">
      <div class="leaderboard">
        <div class="has-text-left p-lg-4">
          <h1 class="title is-3">{{ $t("standings") }}</h1>
          <h5 class="is-5">{{ seasonName }}</h5>

          <div class="my-5">
            <Button
              :class="`p-button-sm mr-2 p-button-${standingsFilter === 'League' ? 'secondary' : 'outlined'}`"
              @click="standingsFilter = 'League'"
            >
              {{ $t("AppMenu.league") }}
            </Button>

            <Button
              :class="`p-button-sm mr-2 p-button-${standingsFilter === 'Division' ? 'secondary' : 'outlined'}`"
              @click="standingsFilter = 'Division'"
            >
              {{ $t("Division.header") }}
            </Button>

            <Button
              :class="`p-button-sm p-button-${standingsFilter === 'Conference' ? 'secondary' : 'outlined'}`"
              @click="standingsFilter = 'Conference'"
              v-if="conferencesStats.length"
            >
              {{ $t("conference") }}
            </Button>
          </div>
        </div>

        <ProgressSpinner v-if="loading" />
        <div v-else>
          <div v-if="standingsFilter === 'League' && leagueStandings.length">
            <h5 class="title p-text-left is-5 mt-3">
              {{ leagueName }}
            </h5>

            <DataTable
              :scrollable="true"
              class="p-datatable-sm p-datatable-striped whiteBg mt-3"
              dataKey="id"
              :value="leagueStandings"
            >
              <Column field="name" :header="$t('standingHeaders.team')"></Column>
              <Column field="gamesPlayed" :header="$t('standingHeaders.gamesPlayed')"></Column>
              <Column field="win" :header="$t('standingHeaders.win')"></Column>
              <Column field="loss" :header="$t('standingHeaders.loss')"></Column>
              <Column field="tie" :header="$t('standingHeaders.tie')"></Column>
              <Column field="win_pct" :header="$t('standingHeaders.winPct')"></Column>
              <Column field="overtime_win" :header="$t('standingHeaders.otWin')"></Column>
              <Column field="overtime_loss" :header="$t('standingHeaders.otLoss')"></Column>
              <Column field="home">
                <template #header="slotProps">
                  {{ $t("standingHeaders.home") }}
                  <font-awesome-icon
                    :icon="['far', 'question-circle']"
                    class="tooltip-icon"
                    v-tooltip.top="$t('standingHeaders.tooltipText')"
                  />
                </template>
              </Column>
              <Column field="away">
                <template #header="slotProps">
                  {{ $t("standingHeaders.away") }}
                  <font-awesome-icon
                    :icon="['far', 'question-circle']"
                    class="tooltip-icon"
                    v-tooltip.top="$t('standingHeaders.tooltipText')"
                  />
                </template>
              </Column>
              <Column field="points" :header="$t('standingHeaders.points')"></Column>
              <Column field="rank" :header="$t('standingHeaders.rank')"></Column>
            </DataTable>
          </div>

          <div v-if="standingsFilter === 'Division' && divisionsStandings.length">
            <div v-for="d in divisionsStandings" :key="d[0].divisionName">
              <h5 class="title p-text-left is-5 mt-5">
                {{ d[0].divisionName }}
              </h5>

              <DataTable
                :scrollable="true"
                class="p-datatable-sm p-datatable-striped whiteBg mt-3"
                dataKey="id"
                :value="leagueStandings"
              >
                <Column field="name" :header="$t('standingHeaders.team')"></Column>
                <Column field="gamesPlayed" :header="$t('standingHeaders.gamesPlayed')"></Column>
                <Column field="win" :header="$t('standingHeaders.win')"></Column>
                <Column field="loss" :header="$t('standingHeaders.loss')"></Column>
                <Column field="tie" :header="$t('standingHeaders.tie')"></Column>
                <Column field="win_pct" :header="$t('standingHeaders.winPct')"></Column>
                <Column field="overtime_win" :header="$t('standingHeaders.otWin')"></Column>
                <Column field="overtime_loss" :header="$t('standingHeaders.otLoss')"></Column>
                <Column field="home">
                  <template #header="slotProps">
                    {{ $t("standingHeaders.home") }}
                    <font-awesome-icon
                      :icon="['far', 'question-circle']"
                      class="tooltip-icon"
                      v-tooltip.top="$t('standingHeaders.tooltipText')"
                    />
                  </template>
                </Column>
                <Column field="away">
                  <template #header="slotProps">
                    {{ $t("standingHeaders.away") }}
                    <font-awesome-icon
                      :icon="['far', 'question-circle']"
                      class="tooltip-icon"
                      v-tooltip.top="$t('standingHeaders.tooltipText')"
                    />
                  </template>
                </Column>
                <Column field="points" :header="$t('standingHeaders.points')"></Column>
                <Column field="rank" :header="$t('standingHeaders.rank')"></Column>
              </DataTable>
            </div>
          </div>

          <div v-if="standingsFilter === 'Conference' && conferences.length">
            <div v-for="(conference, index) in conferences" :key="conference.id">
              <h5 class="title p-text-left is-5 mt-5">
                {{ conference.name }}
              </h5>

              <DataTable
                :scrollable="true"
                class="p-datatable-sm p-datatable-striped whiteBg mt-5"
                dataKey="id"
                :value="conferencesStats[index]"
              >
                <Column field="name" :header="$t('standingHeaders.team')"></Column>
                <Column field="gamesPlayed" :header="$t('standingHeaders.gamesPlayed')"></Column>
                <Column field="win" :header="$t('standingHeaders.win')"></Column>
                <Column field="loss" :header="$t('standingHeaders.loss')"></Column>
                <Column field="tie" :header="$t('standingHeaders.tie')"></Column>
                <Column field="win_pct" :header="$t('standingHeaders.winPct')"></Column>
                <Column field="overtime_win" :header="$t('standingHeaders.otWin')"></Column>
                <Column field="overtime_loss" :header="$t('standingHeaders.otLoss')"></Column>
                <Column field="home">
                  <template #header="slotProps">
                    {{ $t("standingHeaders.home") }}
                    <font-awesome-icon
                      :icon="['far', 'question-circle']"
                      class="tooltip-icon"
                      v-tooltip.top="$t('standingHeaders.tooltipText')"
                    />
                  </template>
                </Column>
                <Column field="away">
                  <template #header="slotProps">
                    {{ $t("standingHeaders.away") }}
                    <font-awesome-icon
                      :icon="['far', 'question-circle']"
                      class="tooltip-icon"
                      v-tooltip.top="$t('standingHeaders.tooltipText')"
                    />
                  </template>
                </Column>
                <Column field="points" :header="$t('standingHeaders.points')"></Column>
                <Column field="rank" :header="$t('standingHeaders.rank')"></Column>
              </DataTable>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PublicFooter v-bind="$props" />
  </div>
</template>

<script>
import api from "@/api";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import PublicHeader from "@/components/PublicHeader.vue";
import PublicFooter from "@/components/PublicFooter.vue";
export default {
  metaInfo() {
    return {
      ...this.metaContent,
      title: `${this.leagueName} Standings | Diamond Scheduler`,
    };
  },
  props: [
    "seasonId",
    "logoUrl",
    "showTeamsPage",
    "showStandingsPage",
    "showSchedulesPage",
    "showScoresPage",
    "showRostersPage",
    "domain",
    "leagueName",
    "metaContent",
  ],
  components: { PublicFooter, PublicHeader, DataTable, Column },
  data() {
    return {
      standings: {},
      standingsFilter: this.$t("AppMenu.league"),
      filters: {
        standings: [this.$t("AppMenu.league"), this.$t("Division.header")],
      },
      loading: true,
      leagueName: "",
      seasonName: "",
      leagueStandings: [],
      divisionsStandings: [],
      conferences: [],
      conferencesStats: [],
      scoringSystem: {
        win_points: 3,
        loss_points: 0,
        tie_points: 2,
        overtime_win_points: 1,
        overtime_loss_points: 0,
      },
    };
  },
  methods: {
    async fetchData(seasonId) {
      await api.getPublicDivisions(seasonId).then(response => {
        if (!response.data.length) return;
        const seasonScoringSystem = response.data[0].scoring_system;

        if (seasonScoringSystem.win_points) {
          this.scoringSystem = seasonScoringSystem;
        }
        const allGames = response.data.map(d => d.games).flat();

        this.leagueName = response.data[0].league_name;
        this.seasonName = response.data[0].season.name;
        this.divisionsStandings = this.leaderboardStatsByDivision(allGames, this.scoringSystem);
        this.leagueStandings = this.gamesLeaderboardStats(allGames, this.scoringSystem)
          .sort((a, b) => b.points - a.points || b.gamesPlayed - a.gamesPlayed)
          .map((team, index) => ({ ...team, rank: index + 1 }));
        this.loading = false;
      });

      await api.getConferences(seasonId).then(response => {
        this.conferences = response.data;
        this.conferencesStats = response.data.map(conference => {
          const stats = this.gamesLeaderboardStats(conference.games, this.scoringSystem);
          const teamNames = conference.teams.map(t => t.name);
          const filteredStats = stats.filter(stat => teamNames.includes(stat.name));

          const filteredTeams = filteredStats.map(team => team.name);
          const teamsWithNoGames = teamNames.filter(n => !filteredTeams.includes(n));
          teamsWithNoGames.forEach(teamName => filteredStats.push(this.buildEmptyGameStatRow(teamName)));

          return filteredStats.map((team, index) => ({ ...team, rank: index + 1 }));
        });
      });
    },
  },
  mounted() {
    this.fetchData(this.seasonId);
  },
};
</script>
